// https://github.com/championswimmer/vuex-module-decorators

import Vue from "vue";
import axios from "axios";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import UserStore from "@/store/modules/users";

import { Submission } from "@/models/definitions";

interface RawSubmission {
  image: string;
  exif: string;
}

interface SubmissionResponse extends RawSubmission {
  user_id: number;
  challenge_id: number;
}

interface RawSubmissionsForUserDict {
  [userId: number]: { [challengeId: number]: RawSubmission };
}

interface SubmissionsForUserDict {
  [challengeId: number]: Submission;
}

interface SubmissionsDict {
  [userId: number]: SubmissionsForUserDict;
}

@Module
export default class Submissions extends VuexModule {
  submissions: SubmissionsDict = {};

  get userId(): number | undefined {
    const userStore = this.context.rootGetters as UserStore;
    return userStore.currentUser?.id;
  }

  @Mutation
  setSubmissions(submissionDict: RawSubmissionsForUserDict) {
    if (submissionDict === undefined) {
      return;
    }

    const userId = +Object.keys(submissionDict)[0];

    const submissionsForUser: SubmissionsForUserDict = {};
    for (const [challengeId, submission] of Object.entries(
      submissionDict[userId]
    )) {
      submissionsForUser[+challengeId] = {
        ...submission,
        exif: submission.exif ? JSON.parse(submission.exif) : null,
      };
    }
    Vue.set(this.submissions, userId, submissionsForUser);
  }

  @Mutation
  updateSubmission(submission: SubmissionResponse) {
    if (submission && this.submissions[submission.user_id] !== undefined) {
      Vue.set(this.submissions[submission.user_id], submission.challenge_id, {
        image: submission.image,
        exif: submission.exif ? JSON.parse(submission.exif) : null,
      });
    } else {
      console.error("Received empty submission for update!");
    }
  }

  @Action({ commit: "setSubmissions" })
  async fetchSubmissions() {
    if (!this.userId) {
      return;
    }

    const config = {
      headers: {
        accept: "application/json",
      },
      params: {
        user: this.userId,
        key: "challenge_id",
      },
    };

    const response = await axios.get<RawSubmissionsForUserDict>(
      process.env.VUE_APP_API_ROOT_URL + "api/submissions",
      config
    );
    return response.data;
  }

  @Action({ commit: "updateSubmission" })
  async submitImage({
    challenge,
    image,
    exifData,
  }: {
    challenge: number;
    image: Blob;
    exifData: string;
  }) {
    if (this.userId === undefined) {
      return {};
    }

    const formData = new FormData();
    formData.append("image", image);
    formData.append("exif", exifData);
    const config = {
      headers: {
        accept: "application/json",
        "content-type": "multipart/form-data",
      },
      params: {
        user: this.userId,
        challenge,
      },
    };
    const response = await axios.post(
      process.env.VUE_APP_API_ROOT_URL + "api/submission/upload",
      formData,
      config
    );
    return response.data;
  }

  get allSubmissions() {
    if (this.userId === undefined) {
      return {};
    }

    return this.submissions[this.userId];
  }
}
